import { useIsMobile } from 'pricing-pages-ui-components/hooks/useMedia';
import { useEffect } from 'react';
import { useExperimentContext } from '../../context/ExperimentContext';
import { useIsFreePortal } from '../../v2/hooks/inventory/useProductInventory';
import { useIsInAppAuth } from '../../v2/hooks/user/useUserInfo';
import { laboratoryClient } from '../laboratoryClient';
import { getIsAcceptanceTest } from '../testUtils';

/**
 * Common eligibility criteria.
 */
export const useCommonEligibilityCriteria = () => {
  const isAppAuth = useIsInAppAuth();
  const isMobile = useIsMobile();
  const {
    isFreePortal,
    loading: isInventoryLoading
  } = useIsFreePortal();
  return {
    isAppAuth,
    isMobile,
    isFreePortal,
    isInventoryLoading
  };
};

// Dedupes `laboratoryClient.logExposure` to once per treatment per SPA session.
// Laboratory exposure semantics are "user was observed in this cohort during this
// session", not per route view, so SPA navigation between eligible routes should
// not re-log. State only resets on full page reload.
const loggedExposureKeys = new Set();

/**
 * Test-only helper to reset the deduplication state between specs.
 */
export const resetLoggedExposureKeys = () => {
  loggedExposureKeys.clear();
};

/**
 * Log experiment exposure
 */
export const logExperimentExposure = treatmentKey => {
  if (loggedExposureKeys.has(treatmentKey)) {
    return;
  }
  loggedExposureKeys.add(treatmentKey);
  laboratoryClient.logExposure(treatmentKey);
};

/**
 * Get experiment data
 *
 * @param treatmentKey - The treatment key for the experiment
 * @param groupParameterKey - The group parameter key for the experiment, defaults to 'group'
 * @param groupParameterOptions - The group parameter options for the experiment
 * @param experimentEligibilityCondition - The eligibility condition for the experiment, defaults to a condition that is always met
 */
export const useExperiment = ({
  treatmentKey,
  groupParameterKey = 'group',
  groupParameterOptions,
  experimentEligibilityCondition = () => ({
    isConditionMet: true,
    eligibilityConditionData: {}
  })
}) => {
  const {
    treatments,
    isLoading,
    hasFetched
  } = useExperimentContext();
  const experiment = treatments[treatmentKey];
  const group = experiment === null || experiment === void 0 ? void 0 : experiment.parameters[groupParameterKey];

  // Eligibility criteria
  const isAcceptanceTest = getIsAcceptanceTest();
  const {
    isConditionMet,
    eligibilityConditionData
  } = experimentEligibilityCondition();
  const isEligible = hasFetched && !isAcceptanceTest && isConditionMet;
  const allCapsTreatmentKey = treatmentKey.split('-').join('').toUpperCase();
  const groupParameterOptionsObject = {};
  for (const option of Object.keys(groupParameterOptions)) {
    const capitalizedOption = option.charAt(0).toUpperCase() + option.slice(1);
    const experimentCohortKey = `is${allCapsTreatmentKey}${capitalizedOption}`;
    groupParameterOptionsObject[experimentCohortKey] = isEligible && groupParameterOptions[option] === group;
  }
  return Object.assign({
    // experiment data
    [`isEligibleFor${allCapsTreatmentKey}`]: isEligible,
    [`is${allCapsTreatmentKey}Loading`]: isLoading,
    [`is${allCapsTreatmentKey}Fetched`]: hasFetched,
    key: treatmentKey,
    group
  }, groupParameterOptionsObject, eligibilityConditionData);
};

/**
 * Use this hook to log exposure, update eligibility state, and return experiment data
 *
 * @param treatmentKey - The treatment key for the experiment
 * @param experimentEligibilityCondition - The eligibility condition for the experiment, defaults to a condition that is always met
 * @param groupParameterOptions - The group parameter options for the experiment
 * @param groupParameterKey - The group parameter key for the experiment, defaults to 'group'
 * @param shouldLogExposure - Whether to log exposure for the experiment, defaults to true
 */
export const useExperimentEffect = ({
  treatmentKey,
  experimentEligibilityCondition = () => {
    return {
      isConditionMet: true,
      eligibilityConditionData: {}
    };
  },
  groupParameterOptions,
  groupParameterKey = 'group',
  shouldLogExposure = true
}) => {
  const isInApp = useIsInAppAuth();
  const isPublic = !isInApp;
  const {
    registerEligibility
  } = useExperimentContext();
  const experiment = useExperiment({
    treatmentKey,
    experimentEligibilityCondition,
    groupParameterOptions,
    groupParameterKey
  });
  const allCapsTreatmentKey = treatmentKey.split(/[-_]/).join('').toUpperCase();
  const isEligible = experiment[`isEligibleFor${allCapsTreatmentKey}`];
  const whichGroup = Object.keys(groupParameterOptions).find(key => {
    const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
    return experiment[`is${allCapsTreatmentKey}${capitalizedKey}`];
  }) || 'control';

  // No cleanup on unmount: cohort eligibility is sticky for the SPA session
  // (intent-to-treat). A user exposed on one page must keep the cohort tag
  // when navigating to and converting from another, otherwise downstream
  // attribution breaks. Whether the variant *renders* on a given page is a
  // separate component-level concern. See arbor docs/experiments.mdx.
  useEffect(() => {
    if (isEligible && shouldLogExposure) {
      logExperimentExposure(treatmentKey);
    }
    registerEligibility({
      treatmentKey,
      isEligible,
      group: whichGroup,
      isPublic,
      isInApp
    });
  }, [registerEligibility, isEligible, treatmentKey, whichGroup, shouldLogExposure, isInApp, isPublic]);
  return experiment;
};